import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { BellSlashIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { ErrorPush, EstadoPush, activarPush, desactivarPush, leerEstadoPush, probarPush } from '@/utils/push';

// Segundos de espera de la prueba demorada: da tiempo a bloquear el celular
const DEMORA_PRUEBA = 15;

const TEXTOS: Record<Exclude<EstadoPush, 'activo' | 'inactivo'>, string> = {
  bloqueado:
    'Están bloqueadas para el sistema en este dispositivo. Habilitalas desde los ajustes del celular (Notificaciones) o del navegador y volvé a entrar.',
  'ios-sin-instalar':
    'En iPhone solo funcionan con el sistema instalado: en Safari tocá Compartir, después «Agregar a inicio», abrilo desde ese ícono y activalas ahí.',
  'no-soportado': 'Este navegador no permite notificaciones. En iPhone hace falta iOS 16.4 o más nuevo.'
};

/**
 * Sección de "Mi perfil": activar las notificaciones push en este dispositivo y
 * mandarse una de prueba. La lógica está en utils/push.ts.
 */
const NotificacionesPush: React.FC = () => {
  const location = useLocation();
  const seccion = useRef<HTMLElement>(null);
  const [estado, setEstado] = useState<EstadoPush | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [faltan, setFaltan] = useState(0);        // segundos que faltan para la prueba demorada

  const refrescar = useCallback(() => { leerEstadoPush().then(setEstado).catch(() => setEstado('no-soportado')); }, []);
  useEffect(refrescar, [refrescar]);

  // El menú del usuario trae directo a esta sección
  useEffect(() => {
    if (location.hash === '#notificaciones') seccion.current?.scrollIntoView({ block: 'start' });
  }, [location.hash, location.key]);

  useEffect(() => {
    if (faltan <= 0) return undefined;
    const t = window.setTimeout(() => setFaltan((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [faltan]);

  const activar = async () => {
    setOcupado(true);
    try {
      await activarPush();
      toast.success('Notificaciones activadas en este dispositivo');
    } catch (error: any) {
      if (error instanceof ErrorPush) {
        toast.error(error.motivo === 'bloqueado' ? 'Las notificaciones están bloqueadas en este dispositivo' : 'Hace falta que aceptes el permiso para activarlas');
      } else {
        toast.error(error?.response?.data?.error || 'No se pudieron activar las notificaciones en este navegador');
      }
    } finally {
      setOcupado(false);
      refrescar();
    }
  };

  const desactivar = async () => {
    setOcupado(true);
    try {
      await desactivarPush();
      toast.success('Este dispositivo ya no recibe notificaciones');
    } finally {
      setOcupado(false);
      refrescar();
    }
  };

  const probar = async (demora: number) => {
    setOcupado(true);
    try {
      await probarPush(demora);
      if (demora > 0) setFaltan(demora);
      else toast.success('Prueba enviada: tiene que aparecer en unos segundos');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo mandar la prueba');
      refrescar();
    } finally {
      setOcupado(false);
    }
  };

  return (
    <section ref={seccion} id="notificaciones" className="rounded-lg border border-border/40 bg-background p-5 shadow-sm">
      <div className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold leading-tight">Notificaciones en este dispositivo</h2>
          <p className="text-xs text-muted-foreground">
            Los avisos del sistema te llegan aunque lo tengas cerrado o el celular bloqueado. Se activan en cada dispositivo por separado.
          </p>
        </div>

        {estado === null && <p className="text-sm text-muted-foreground">Revisando este dispositivo...</p>}

        {estado === 'activo' && (
          <>
            <p className="flex items-center gap-2 text-sm font-medium">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
              Activadas en este dispositivo
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={() => probar(0)} disabled={ocupado || faltan > 0}>
                Probar ahora
              </Button>
              <Button type="button" variant="secondary" onClick={() => probar(DEMORA_PRUEBA)} disabled={ocupado || faltan > 0}>
                Probar en {DEMORA_PRUEBA} segundos
              </Button>
              <Button type="button" variant="ghost" onClick={desactivar} disabled={ocupado}>
                Desactivar
              </Button>
            </div>
            <p className="text-xs text-muted-foreground" role="status" aria-live="polite">
              {faltan > 0
                ? `La prueba sale en ${faltan} s: bloqueá el celular para verla llegar.`
                : `«Probar en ${DEMORA_PRUEBA} segundos» te da tiempo a bloquear el celular y ver cómo llega.`}
            </p>
          </>
        )}

        {estado === 'inactivo' && (
          <>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <BellSlashIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              Desactivadas en este dispositivo
            </p>
            <Button type="button" onClick={activar} disabled={ocupado}>
              {ocupado ? 'Activando...' : 'Activar notificaciones'}
            </Button>
          </>
        )}

        {estado !== null && estado !== 'activo' && estado !== 'inactivo' && (
          <p className="flex items-start gap-2 text-sm">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
            <span>{TEXTOS[estado]}</span>
          </p>
        )}
      </div>
    </section>
  );
};

export default NotificacionesPush;
